/**
 * Stats components for ToolCallCard.
 */

import type { CSSProperties } from "react";

import type { ToolCallContent } from "./ToolCallCard.types";

import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

/** A count, in its operation's colour (``--stat-color``): +3, ~1, -2, →5. */
const STAT_CLASS =
  "font-[family-name:monospace] [font-weight:700] text-[11px] text-(--stat-color)";

/** The grey of the "·" before a count (``text-muted-foreground/80``), for a
 * count that reads as history or only says what was looked at. */
const MUTED_STAT_COLOR =
  "color-mix(in oklab, var(--muted-foreground) 80%, transparent)";

// ── Tool call completion stats ────────────────────────────────────────────────

export function ToolCallCardStats({
  children,
}: {
  children: ToolCallContent[];
}) {
  if (!children || children.length === 0) return null;

  const done = children.filter(
    (c) => c.type === "tool_call" && (c.toolOutput || c.toolError),
  ).length;
  const total = children.filter((c) => c.type === "tool_call").length;
  const errors = children.filter(
    (c) => c.type === "tool_call" && c.toolError,
  ).length;

  if (total === 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      {done > 0 && (
        <Typography
          level="body-xs"
          className="font-mono [font-weight:700] text-[11px] text-foreground"
        >
          +{done}
        </Typography>
      )}
      {errors > 0 && (
        <Typography
          level="body-xs"
          className="font-mono [font-weight:700] text-[11px] text-destructive"
        >
          -{errors}
        </Typography>
      )}
    </div>
  );
}

// ── Bulk change stats (created/modified/deleted) ──────────────────────────────

export function ToolCallCardBulkStats({
  children,
}: {
  children: ToolCallContent[];
}) {
  if (!children || children.length === 0) return null;

  let created = 0;
  let deleted = 0;
  let modified = 0;

  const countChanges = (items: ToolCallContent[]) => {
    for (const child of items) {
      if (child.type === "tool_call" && child.toolOutput && !child.toolError) {
        const toolName = child.toolName || "";
        if (toolName.startsWith("create_")) {
          created++;
        } else if (toolName.startsWith("delete_")) {
          deleted++;
        } else if (toolName.startsWith("update_")) {
          modified++;
        }
      }
      if (child.children) {
        countChanges(child.children);
      }
    }
  };

  countChanges(children);

  if (created === 0 && deleted === 0 && modified === 0) return null;

  // Build stats array in order: create, modify, delete
  const stats: { symbol: string; count: number; color: string }[] = [];
  if (created > 0)
    stats.push({
      symbol: "+",
      count: created,
      color: "var(--foreground)",
    });
  if (modified > 0)
    stats.push({
      symbol: "~",
      count: modified,
      color: "var(--foreground)",
    });
  if (deleted > 0)
    stats.push({
      symbol: "-",
      count: deleted,
      color: "var(--destructive)",
    });

  return (
    <>
      {stats.map((stat) => (
        <span key={stat.symbol} className="inline-flex items-center gap-1">
          <Typography level="body-xs" className="text-muted-foreground/80">
            ·
          </Typography>
          <Typography
            level="body-xs"
            className={STAT_CLASS}
            style={{ "--stat-color": stat.color } as CSSProperties}
          >
            {stat.symbol}
            {stat.count}
          </Typography>
        </span>
      ))}
    </>
  );
}

// ── Tool output stats (→n, ~n, +n, -n) ────────────────────────────────────────

export function ToolCallCardOutputStats({
  toolName,
  toolOutput,
  toolError,
  isPending,
  isLoading = false,
}: {
  toolName?: string | null;
  toolOutput?: Record<string, unknown> | null;
  toolError?: string | null;
  /** When false the change was confirmed/rejected; grey out the symbol. */
  isPending?: boolean;
  /** The call is still running: the "·" and a placeholder for its count. */
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <span className="inline-flex items-center gap-1">
        <Typography level="body-xs" className="text-muted-foreground/80">
          ·
        </Typography>
        <Skeleton
          variant="rectangular"
          width={16}
          height={10}
          className="rounded"
        />
      </span>
    );
  }

  if (!toolOutput || toolError) return null;

  const name = (toolName || "").toLowerCase();

  // Count items from output
  const getItemCount = (): number => {
    if (toolOutput.item) return 1;
    if (Array.isArray(toolOutput.items)) return toolOutput.items.length;
    if (Array.isArray(toolOutput.results)) return toolOutput.results.length;
    // For bulk operations with successful count
    if (typeof toolOutput.successful === "number") return toolOutput.successful;
    return 0;
  };

  const count = getItemCount();
  if (count === 0) return null;

  // For for_each tools, always show as "query" (grey →) since the count
  // represents items iterated, not items changed. BulkStats handles showing changes.
  const getOperationType = (): string => {
    // For for_each_* tools, always treat as iteration (grey arrow)
    // The BulkStats component shows the actual create/update/delete counts
    if (name.startsWith("for_each_")) {
      return "query";
    }

    // Direct tool name detection for regular tools
    if (
      name.startsWith("query_") ||
      name.startsWith("list_") ||
      name.startsWith("get_")
    )
      return "query";
    if (name.startsWith("create_") || name.startsWith("bulk_create_"))
      return "create";
    if (name.startsWith("update_") || name.startsWith("bulk_update_"))
      return "update";
    if (name.startsWith("delete_") || name.startsWith("remove_"))
      return "delete";

    return "unknown";
  };

  const operationType = getOperationType();
  const isQuery = operationType === "query";
  const isCreate = operationType === "create";
  const isUpdate = operationType === "update";
  const isDelete = operationType === "delete";

  // After confirmation/rejection the change is resolved - grey out the symbol
  const resolved = isPending === false;

  // Determine symbol and color
  let symbol = "";
  let color = "";

  if (resolved) {
    color = MUTED_STAT_COLOR;
  }

  if (isQuery) {
    symbol = "→";
    if (!resolved) color = MUTED_STAT_COLOR;
  } else if (isCreate) {
    symbol = "+";
    if (!resolved) color = "var(--foreground)";
  } else if (isUpdate) {
    symbol = "~";
    if (!resolved) color = "var(--foreground)";
  } else if (isDelete) {
    symbol = "-";
    if (!resolved) color = "var(--destructive)";
  } else {
    // Default for unknown operations
    symbol = "→";
    if (!resolved) color = MUTED_STAT_COLOR;
  }

  return (
    <span className="inline-flex items-center gap-1">
      <Typography level="body-xs" className="text-muted-foreground/80">
        ·
      </Typography>
      <Typography
        level="body-xs"
        className={STAT_CLASS}
        style={{ "--stat-color": color } as CSSProperties}
      >
        {symbol}
        {count}
      </Typography>
    </span>
  );
}
